import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../stores/auth';

// Import pages
import Welcome from '../pages/Welcome.vue';
import Pricing from '../pages/Pricing.vue';
import About from '../pages/About.vue';
import Contact from '../pages/Contact.vue';
import Integrations from '../pages/Integrations.vue';
import Resources from '../pages/Resources.vue';
import Guides from '../pages/Guides.vue';
import Careers from '../pages/Careers.vue';
import Enterprise from '../pages/Enterprise.vue';
import Blog from '../pages/Blog.vue';
import Community from '../pages/Community.vue';
import Terms from '../pages/Terms.vue';
import Home from '../pages/Home.vue';
import Profile from '../pages/Profile.vue';
import Subscription from '../pages/Subscription.vue';
import ScanDetails from '../pages/ScanDetails.vue';
import Companies from '../pages/Companies.vue';
import CompanyVendors from '../pages/CompanyVendors.vue';
import CompanyAccountCodes from '../pages/CompanyAccountCodes.vue';
import ManualInvoice from '../pages/ManualInvoice.vue';

// Import auth pages
import Login from '../pages/auth/Login.vue';
import Register from '../pages/auth/Register.vue';
import ForgotPassword from '../pages/auth/ForgotPassword.vue';
import ResetPassword from '../pages/auth/ResetPassword.vue';

const routes = [
  {
    path: '/',
    name: 'Welcome',
    component: Welcome,
    meta: { requiresAuth: false },
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false, hideNavbar: true },
  },
  {
    path: '/register',
    name: 'Register',
    component: Register,
    meta: { requiresAuth: false, hideNavbar: true },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { requiresAuth: false, hideNavbar: true },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { requiresAuth: false, hideNavbar: true },
  },
  {
    path: '/pricing',
    name: 'Pricing',
    component: Pricing,
    meta: { requiresAuth: false },
  },
  {
    path: '/about',
    name: 'About',
    component: About,
    meta: { requiresAuth: false },
  },
  {
    path: '/terms',
    name: 'Terms',
    component: Terms,
    meta: { requiresAuth: false },
  },
  {
    path: '/contact',
    name: 'Contact',
    component: Contact,
    meta: { requiresAuth: false },
  },
  {
    path: '/integrations',
    name: 'Integrations',
    component: Integrations,
    meta: { requiresAuth: false },
  },
  {
    path: '/resources',
    name: 'Resources',
    component: Resources,
    meta: { requiresAuth: false },
  },
  {
    path: '/guides',
    name: 'Guides',
    component: Guides,
    meta: { requiresAuth: false },
  },
  {
    path: '/careers',
    name: 'Careers',
    component: Careers,
    meta: { requiresAuth: false },
  },
  {
    path: '/enterprise',
    name: 'Enterprise',
    component: Enterprise,
    meta: { requiresAuth: false },
  },
  {
    path: '/blog',
    name: 'Blog',
    component: Blog,
    meta: { requiresAuth: false },
  },
  {
    path: '/community',
    name: 'Community',
    component: Community,
    meta: { requiresAuth: false },
  },
  {
    path: '/home',
    name: 'Home',
    component: Home,
    meta: { requiresAuth: true },
  },
  {
    path: '/scans',
    redirect: '/',
  },
  {
    path: '/profile',
    name: 'Profile',
    component: Profile,
    meta: { requiresAuth: true },
  },
  {
    path: '/subscription',
    name: 'Subscription',
    component: Subscription,
    meta: { requiresAuth: true },
  },
  {
    path: '/scans/:id',
    name: 'ScanDetails',
    component: ScanDetails,
    meta: { requiresAuth: true },
  },
  {
    path: '/manual-invoice',
    name: 'ManualInvoice',
    component: ManualInvoice,
    meta: { requiresAuth: true },
  },
  {
    path: '/companies',
    name: 'Companies',
    component: Companies,
    meta: { requiresAuth: true },
  },
  {
    path: '/companies/:companyId/vendors',
    name: 'CompanyVendors',
    component: CompanyVendors,
    meta: { requiresAuth: true },
  },
  {
    path: '/companies/:companyId/account-codes',
    name: 'CompanyAccountCodes',
    component: CompanyAccountCodes,
    meta: { requiresAuth: true },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Without this a new page keeps the previous page's scroll offset, so following
  // a footer link lands you part-way down the page instead of at its top.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition; // back/forward returns where you were
    if (to.hash) return { el: to.hash, behavior: 'smooth' };
    return { top: 0 };
  },
});

// Navigation guard - check if route requires authentication
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  
  // If route doesn't require auth and we're not going to root, skip all auth checks
  if (!to.meta.requiresAuth && to.path !== '/' && to.path !== '/login' && to.path !== '/register') {
    next();
    return;
  }
  
  // Only try to fetch user if we haven't already and we're not currently loading
  if (!authStore.user && !authStore.loading) {
    try {
      await authStore.fetchUser();
    } catch (error) {
      // User not authenticated - that's okay
      authStore.user = null;
    }
  }
  
  // If user is authenticated and trying to access welcome/login/register pages, redirect to home
  if (authStore.isAuthenticated && (to.path === '/' || to.path === '/login' || to.path === '/register')) {
    next('/home');
    return;
  }
  
  // If route requires auth and user is not authenticated, redirect to welcome
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/');
    return;
  }
  
  // Otherwise proceed
  next();
});

export default router;
